import { supabase } from '../lib/supabase';

export const authService = {
    /**
     * Sign in with email and password
     * @param {string} identifier 
     * @param {string} password 
     * @returns {Promise<{data: any, error: any}>}
     */
    async signIn(identifier, password) {
        try {
            const normalized = String(identifier || '').trim().toLowerCase();
            let email = normalized;
            let resolvedFromIdentifier = false;
            if (!normalized.includes('@')) {
                const { data: resolved, error: resolveError } = await supabase.rpc('resolve_login_identifier', { login_identifier: normalized });
                if (resolveError) {
                    const message = String(resolveError.message || '');
                    if (resolveError.code === 'PGRST202' || /Could not find the function public\.resolve_login_identifier/i.test(message)) {
                        return {
                            error: {
                                ...resolveError,
                                message: 'Student-number login is not configured on the server. Apply supabase/migrations/20260101000002_fix_login_identifier_resolver.sql, then refresh the Supabase schema.'
                            }
                        };
                    }
                    throw resolveError;
                }
                if (!resolved) return { error: { message: 'No active account matches that email, PF number or registration number.' } };
                email = resolved;
                resolvedFromIdentifier = true;
            }
            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password,
            });
            if (error && resolvedFromIdentifier && /invalid login credentials/i.test(String(error.message || ''))) {
                return {
                    data,
                    error: {
                        ...error,
                        message: 'Student number found, but the password is incorrect. Use the assigned password or reset it with “Forgot your password?”.'
                    }
                };
            }
            return { data, error };
        } catch (error) {
            return { error };
        }
    },

    /**
     * Sign out current user
     * @returns {Promise<{error: any}>}
     */
    async signOut() {
        try {
            const { error } = await supabase.auth.signOut();
            return { error };
        } catch (error) {
            return { error };
        }
    },

    /**
     * Get user profile by ID
     * @param {string} userId 
     * @returns {Promise<{data: any, error: any}>}
     */
    async getUserProfile(userId) {
        try {
            const { data, error } = await supabase
                .from('user_profiles')
                .select('*')
                .eq('id', userId)
                .single();
            if (error || !data) return { data, error };

            // Include the staff department in the auth profile.  Dashboard
            // routing needs this context because an HOD title can belong to
            // either an academic or an operational department.
            const { data: staff } = await supabase
                .from('staff')
                .select('*')
                .eq('profile_id', userId)
                .maybeSingle();
            const departmentId = staff?.department_id || data?.department_id;
            const { data: department } = departmentId
                ? await supabase
                    .from('departments')
                    .select('id, name, code, faculty_id')
                    .eq('id', departmentId)
                    .maybeSingle()
                : { data: null };

            return {
                data: {
                    ...data,
                    staff: staff ? { ...staff, department: department || null } : null,
                    department: department || null,
                },
                error: null,
            };
        } catch (error) {
            return { error };
        }
    },

    /**
     * Update user profile
     * @param {string} userId 
     * @param {object} updates 
     * @returns {Promise<{data: any, error: any}>}
     */
    async updateUserProfile(userId, updates) {
        try {
            const { data, error } = await supabase
                .from('user_profiles')
                .update(updates)
                .eq('id', userId)
                .select()
                .single();
            return { data, error };
        } catch (error) {
            return { error };
        }
    },

    /**
     * Change user password
     * @param {string} newPassword 
     * @returns {Promise<{data: any, error: any}>}
     */
    async changePassword(newPassword) {
        try {
            const { data, error } = await supabase.auth.updateUser({
                password: newPassword
            });
            if (!error && data?.user?.id) {
                const { error: profileError } = await supabase.from('user_profiles').update({password_change_required:false,is_first_login:false}).eq('id',data.user.id);
                if (profileError) return { data, error:profileError };
            }
            return { data, error };
        } catch (error) {
            return { error };
        }
    }
};
